import React, { useState } from "react";
import "./About.css";
import {
  FaArrowRight,
  FaCloud,
  FaLaptopCode,
  FaUsersCog,
  FaChartLine,
  FaShieldAlt,
  FaServer,
  FaCheckCircle,
  FaQuestionCircle,
  FaGlobeAmericas,
  FaClock,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px -10% 0px" },
  transition: { duration: 0.55, ease: "easeOut", delay: d },
});

const About = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo?.name || "Our Team";

  const [openFaq, setOpenFaq] = useState(null);

  const systems = [
    {
      icon: <FaCloud />,
      title: "Modern Microsoft workplace",
      desc:
        "Microsoft 365, Azure and Dynamics wired together so people, files and security live in one governed place.",
      tags: ["M365 & Teams", "Azure", "Dynamics 365"],
      color: "#2563eb",
    },
    {
      icon: <FaLaptopCode />,
      title: "Web & product layer",
      desc:
        "WordPress, web apps and SaaS-style portals that present your brand, capture leads and serve customers.",
      tags: ["Websites", "SaaS / MVPs", "Client portals"],
      color: "#0ea5e9",
    },
    {
      icon: <FaChartLine />,
      title: "CRM & revenue operations",
      desc:
        "Salesforce, HubSpot, Dynamics, Zoho and custom CRMs aligned to how your team actually sells and supports.",
      tags: ["CRM design", "Playbooks", "Reporting"],
      color: "#22c55e",
    },
    {
      icon: <FaShieldAlt />,
      title: "Finance & security backbone",
      desc:
        "US bookkeeping, antivirus, EDR and backups so numbers stay clean and devices stay protected.",
      tags: ["US books", "Antivirus & EDR", "Backups"],
      color: "#f97316",
    },
  ];

  const differentiators = [
    {
      icon: <FaUsersCog />,
      title: "One partner across the stack",
      desc: "Web, Microsoft, CRM, accounting and security handled by one team that sees the whole picture.",
    },
    {
      icon: <FaServer />,
      title: "Built for non-technical teams",
      desc: "Plain-English roadmaps, clear ownership and documentation your team can actually follow.",
    },
    {
      icon: <FaCheckCircle />,
      title: "Security & compliance aware",
      desc: "Principles aligned with US data protection, access control and audit-friendly bookkeeping.",
    },
    {
      icon: <FaChartLine />,
      title: "Numbers before opinions",
      desc: "Dashboards and reports that connect activity to revenue, margins and utilisation.",
    },
  ];

  const stats = [
    { value: "8+ yrs", label: "Delivering digital systems" },
    { value: "120+", label: "Web / product builds" },
    { value: "95%", label: "Ongoing client relationships" },
    { value: "3", label: "Core time-zones we cover (US, EU, AU)" },
  ];

  const testimonials = [
    {
      quote:
        "They became our quiet IT and systems team in the background — from Microsoft to web to bookkeeping, everything finally talks to each other.",
      author: "COO, US Services Company",
    },
    {
      quote:
        "We launched a new site, CRM and reporting stack in under 90 days. The documentation and training meant my team actually uses it.",
      author: "Founder, B2B SaaS",
    },
    {
      quote:
        "They’re the only vendor who understands our accountant, our sales team and our tools at the same time.",
      author: "Managing Partner, US Agency",
    },
  ];

  const faqs = [
    {
      q: "Which locations and time zones do you support?",
      a: "Most of our work is for US-based companies with teams in the US, Canada and Europe. We overlap US hours daily and can align to specific time zones for standing calls.",
    },
    {
      q: "Do you replace our existing IT, accountant or agency?",
      a: "Not necessarily. Many clients keep their CPA, IT vendor or marketing team. We plug into that mix, connect systems and remove manual work and confusion.",
    },
    {
      q: "What does a typical first project look like?",
      a: "Usually a 6–10 week engagement: auditing your tools, stabilising Microsoft and security, then delivering a clear website / CRM / finance roadmap with 1–2 quick wins shipped.",
    },
    {
      q: "How do you handle security and access?",
      a: "We minimise access, use shared password managers where required, prefer role-based access control and document every integration we touch so you stay in control.",
    },
  ];

  const steps = [
    {
      title: "Listen & map reality",
      desc:
        "We start with how you actually work today: tools, pain points, team skills, budgets and constraints.",
    },
    {
      title: "Design the connected stack",
      desc:
        "We recommend changes to Microsoft, web, CRM, finance and security as one picture—not five separate projects.",
    },
    {
      title: "Implement in small, safe phases",
      desc:
        "Migrations, new builds and automations are rolled out in controlled steps with backup and rollback plans.",
    },
    {
      title: "Support, train & improve",
      desc:
        "We stay on to own issues, train your team and keep dashboards, security and documentation up to date.",
    },
  ];

  return (
    <section className="about-v3">
      {/* HERO */}
      <motion.header className="hero-v3" {...fade()}>
        <div className="hero-wrap">
          <span className="pill">About {brand}</span>
          <h1>
            A systems partner for{" "}
            <span className="focus">Microsoft, web, CRM, finance &amp; security</span>.
          </h1>
          <p className="lead">
            We sit between your tools and your team — designing, building and
            maintaining a connected stack that feels calm, secure and
            measurable instead of chaotic.
          </p>

          <div className="hero-highlights">
            <div className="hero-highlight">
              <FaGlobeAmericas />
              <span>US-focused but globally available</span>
            </div>
            <div className="hero-highlight">
              <FaClock />
              <span>Designed for long-term operations</span>
            </div>
            <div className="hero-highlight">
              <FaShieldAlt />
              <span>Security-first by default</span>
            </div>
          </div>

          <div className="cta-row">
            <a href="/contact" className="btn btn-primary">
              Talk about your stack <FaArrowRight />
            </a>
            <a href="/services" className="btn btn-quiet">
              Browse all services
            </a>
          </div>
        </div>
      </motion.header>

      {/* SNAPSHOT STRIP */}
      <motion.section className="snapshot" {...fade(0.05)}>
        <div className="chip">
          <FaCloud />
          <span>Microsoft, web, CRM, finance &amp; security under one roof</span>
        </div>
        <div className="chip">
          <FaUsersCog />
          <span>Built with non-technical teams in mind</span>
        </div>
        <div className="chip">
          <FaCheckCircle />
          <span>Documented and handover-friendly</span>
        </div>
        <div className="chip">
          <FaChartLine />
          <span>Dashboards tied to real business metrics</span>
        </div>
      </motion.section>

      {/* ENGAGEMENT JOURNEY */}
      <motion.section className="timeline" {...fade(0.1)}>
        <h2>How we normally work with clients</h2>
        <p className="timeline-sub">
          Simple phases that keep risk low and stakeholders aligned while we
          stabilise and upgrade your systems.
        </p>
        <ol className="steps">
          {steps.map((step, idx) => (
            <li key={step.title}>
              <div className="dot">{idx + 1}</div>
              <div className="step-body">
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </motion.section>

      {/* SYSTEMS GRID */}
      <motion.section className="os-grid" {...fade(0.15)}>
        <h2>Systems we help you run</h2>
        <p className="lead">
          Every project slots into one or more of these pillars so your stack
          stays coherent as you grow.
        </p>
        <div className="grid">
          {systems.map((c) => (
            <motion.article
              className="os-card"
              key={c.title}
              whileHover={{ y: -4 }}
            >
              <div className="os-icon" style={{ backgroundColor: c.color }}>
                {c.icon}
              </div>
              <h4>{c.title}</h4>
              <p>{c.desc}</p>
              <div className="tags">
                {c.tags.map((t) => (
                  <span className="tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      {/* DIFFERENTIATORS */}
      <motion.section className="diff" {...fade(0.2)}>
        <h2>Why teams like working with us</h2>
        <div className="diff-list">
          {differentiators.map((d) => (
            <div className="diff-item" key={d.title}>
              <div className="diff-icon">{d.icon}</div>
              <div className="diff-copy">
                <h5>{d.title}</h5>
                <p>{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* METRICS RIBBON */}
      <motion.section className="ribbon" {...fade(0.25)}>
        {stats.map((s) => (
          <div key={s.label} className="kpi">
            <div className="kpi-value">{s.value}</div>
            <div className="kpi-label">{s.label}</div>
          </div>
        ))}
      </motion.section>

      {/* TESTIMONIALS */}
      <motion.section className="testimonials" {...fade(0.3)}>
        <h2>What clients say about working with us</h2>
        <div className="quotes">
          {testimonials.map((t) => (
            <blockquote key={t.author} className="quote">
              <p>“{t.quote}”</p>
              <footer>— {t.author}</footer>
            </blockquote>
          ))}
        </div>
      </motion.section>

      {/* FAQ */}
      <motion.section className="faq" {...fade(0.35)}>
        <h2>Practical questions we get a lot</h2>
        <div className="faq-list">
          {faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div
                key={f.q}
                className={`faq-item ${open ? "open" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => setOpenFaq(open ? null : i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setOpenFaq(open ? null : i);
                }}
                aria-expanded={open}
              >
                <div className="faq-q">
                  <FaQuestionCircle />
                  <span>{f.q}</span>
                </div>
                <div className="faq-a">
                  <p>{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section className="about-cta" {...fade(0.4)}>
        <div className="cta-inner">
          <div>
            <h3>Want a single, connected systems roadmap?</h3>
            <p className="muted">
              Share where you are today and we’ll map Microsoft, web, CRM,
              finance and security into one clear plan.
            </p>
          </div>
          <div className="cta-actions">
            <a href="/contact" className="btn btn-primary">
              Book a call <FaArrowRight />
            </a>
            <a href="/services" className="btn btn-quiet">
              Explore services
            </a>
          </div>
        </div>
      </motion.section>
    </section>
  );
};

export default About;
